import OutOfDateBox from 'components/out-of-date/OutOfDateBox';
import { useKeptVersion } from 'api/keptVersion';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { bringToLifeMarkFrom } from 'components/video-gen/wizardEntry';
import AdEditorNavBar from 'components/ad-builder/ad-editor-preview-ad-variant-bar/Navbar';
import AdEditorPreview from 'components/ad-builder/Preview';
import AdEditorToolbar, {
  AdEditorButton,
  ToolbarOrientation,
} from 'components/ad-builder/Toolbar';
import Button from 'components/button/Button';
import Centered from 'components/centered/Centered';
import AppPage from 'components/page/app-page/AppPage';
import SaveStatus from 'components/save-status/SaveStatus';
import { nextStepButton } from 'components/save-status/nextStepButton';
import UndoRedo from 'components/undo-redo/UndoRedo';
import { useUndoRedo } from 'hooks/useUndoRedo';
import { CHANGES_WERE_PUBLISHED, WORK_WAS_KEPT } from 'api/waitingChanges';
import { IS_SAFARI, useAutoSave } from 'hooks/useAutoSave';
import { usePendingSave } from 'context/PendingSaveContext';
import { sendKeepaliveSave } from 'utils/keepaliveSave';
import {
  AdsArePublished,
  AdsRefused,
  isBeingRedrawn,
  StoredAd,
  StoredAds,
  adForSending,
  adPrint,
  adsBody,
  adsPrint,
  deleteAd,
  rememberAsStored,
  saveAds,
  screenNameOf,
  travelName,
  whatToStore,
} from 'api/adAutoSave';
import SaveButton from 'components/save-button/SaveButton';
import {
  DELETE_VARIANTS,
  UPDATE_EDITOR_DATA,
} from 'graphql/ad-variants/mutations';
import { GET_EDITOR_DATA } from 'graphql/vacancies/queries';
import CampaignValuesMessages from 'components/ad-builder/CampaignValuesMessages';
import useCampaignValues from 'hooks/useCampaignValues';
import { ACK_NOTICE } from 'graphql/campaign-values/mutations';
import { useVacancyChannelSelection } from 'hooks/useVacancyChannelSelection';
import ChannelChangeRecovery from 'components/channel-toggle-group/ChannelChangeRecovery';
import useBeforeUnloadWarning from 'hooks/useBeforeUnloadWarning';
import useNavigationContext from 'hooks/context/nav-context';
import { useMutation } from 'hooks/sympl-mutation';
import { useQuery } from 'hooks/sympl-query';
import { useApolloClient } from '@apollo/client';
import { getLocalAppSession } from 'utils/storageHelpers';
import {
  earliestStoredPictureExpiry,
  nextLinkRenewalAt,
} from 'utils/templatePreviewHelpers';

import tw, { styled } from 'twin.macro';
import {
  describeMissingFields,
  toSavableVariant,
} from 'utils/adVariantSaveHelpers';
import { AdEditorSavablePreviewState, AdVariant } from 'types/adEditorTypes';
import {
  AdChannel,
  AdCreativeType,
  AdPlacement,
  VISUAL_AD_CHANNELS,
} from 'types/adTypes';

import VariantGenerator from './VariantGenerator';
import { adDeletionMessage } from './adDeletionMessage';
import MissingFields from 'components/page/app-page/MissingFields';
import useGetStartedContext from 'hooks/context/get-started-context';
import useApiList from 'hooks/useApiList';
import { EXPERIENCE } from 'types/targetingTypes';
import { Routes } from 'types/routeTypes';
import useAdEditorContext from 'hooks/context/ad-editor-context';
import useVideoGenContext from 'hooks/context/video-gen-context';
import {
  getChannelIcon,
  getFormattedChannel,
  isVariantSupported,
} from 'utils/adEditorHelpers';
import ToolTip from 'components/tooltip/ToolTip';
import Sidebar from './Sidebar';
import { ToastTypes } from 'types/notificationTypes';
import { useToastNotifications } from 'hooks/notificationHooks';
import { useNavigate } from 'react-router-dom';
import { uniqueId } from 'lodash';

/**
 * SYM-6214. How the editor waits for pictures the server is still drawing.
 *
 * A save queues one render per ad and per design, so they land a second or two after the ad list
 * does. Rather than ask for renders of its own -- the volume SYM-6213 exists to remove -- the
 * editor re-asks the ad LIST, which is one cheap read that carries every picture that has landed.
 *
 * ⚠️ Bounded on purpose, and the bound is the safety property. An unbounded poll is a storm with a
 * timer on it: if a render genuinely fails, nothing ever fills that field and a poll with no cap
 * would ask forever, from every open editor. After the cap the editor stops and the live path takes
 * over for whatever is still missing, which is exactly the behaviour before this ticket.
 */
const PREPARING_POLL_MS = 3000;
const PREPARING_MAX_ATTEMPTS = 5;

interface AdEditorQueryResponse {
  editorData: {
    adVariants: AdVariant[];
  };
}

interface AdEditorQueryPayload {
  vacancy: number;
}

export interface AdEditorDeleteVariantPayload {
  variantId: number;
  input: { vacancy_id: number };
}

export interface AdEditorUpdatePayload {
  input: { vacancy_id: number; adVariants: AdEditorSavablePreviewState[] };
}

export enum AdEditorSideBarTabs {
  VISUAL_OPTIONS = 'Visuals',
  OVERLAY_OPTIONS = 'Overlays',
  BANNER_OPTIONS = 'Banner Options',
  LOGO_OPTIONS = 'Logo',
  TEXT_OPTIONS = 'Text',
  SHAPE_OPTIONS = 'Shape',
  COLOR_OPTIONS = 'Color',
}

const AdEditor: React.FC = () => {
  const navigate = useNavigate();

  const { nextItem, refetchCheckList } = useGetStartedContext();
  const { addToast } = useToastNotifications();

  const { activeVacancy, brands, currentVacancy, vacIsPublished } =
    useNavigationContext();
  const { setPending, registerAbandon, registerSaveNow, switching } =
    usePendingSave();

  const {
    activeTab,
    currentVariant,
    previewChannel,
    setActiveTab,
    setPayload,
    setPreviewChannel,
    initVariants,
    applyStoredPictures,
    applyStoredAds,
    applyFreshResources,
    restoreAds,
    aiWritingOn,
    setPageHasUndo,
    variants,
    hasAdTemplatesV2,
  } = useAdEditorContext();

  const { isAnyVideoRendering } = useVideoGenContext();

  const nonDeletedVariants = useMemo(
    () =>
      [...variants]
        .filter(({ isDeleted }) => !isDeleted)
        .sort(
          (a, b) =>
            Object.values(AdPlacement).indexOf(a.placement) -
            Object.values(AdPlacement).indexOf(b.placement)
        ),
    [variants]
  );

  const missingFields = useMemo(
    () => describeMissingFields(nonDeletedVariants),
    [nonDeletedVariants]
  );

  const {
    loading: loadingEditorData,
    data: editorResponse,
    refetch: refetchEditorData,
  } = useQuery<AdEditorQueryResponse, AdEditorQueryPayload>(GET_EDITOR_DATA, {
    skip: !activeVacancy,
    variables: { vacancy: activeVacancy! },
    fetchPolicy: 'network-only',
  });

  const targeting = useMemo(() => {
    return currentVacancy?.targeting;
  }, [currentVacancy]);

  const hasUnsavedChanges = useMemo(
    () => !!variants.find(({ isDirty, isDeleted }) => isDirty || isDeleted),
    [variants]
  );

  /*
   * Auto-save for the ad builder, on a campaign that was NEVER published
   * (the foundation, steps A and B; Steven's go-ahead of 4 Oct 2026).
   *
   * What the server holds of each ad, as far as this screen knows, and the
   * campaign the ads on screen were loaded for. Picking another campaign
   * does not rebuild this screen, so for a moment the ads on screen are
   * still the previous campaign's.
   */
  const storedAds = useRef<StoredAds>({ prints: new Map(), looks: new Map() });
  /**
   * New ads that were sent at least once. The server may hold such an ad
   * even when no answer came back (a line that dropped, a tab that closed).
   */
  const everSent = useRef(new Set<string>());
  const [adsOf, setAdsOf] = useState<number | null>(null);
  /** Ads whose deletion the server confirmed: never asked for again. */
  const reallyDeleted = useRef(new Set<number>());
  /** Raised when deletions were carried out: the undo steps start again. */
  const [undoEra, setUndoEra] = useState(0);
  /** The server refused the screen's own store: published after all. */
  const [publishedAfterAll, setPublishedAfterAll] = useState<number | null>(
    null
  );
  /** The server said the campaign RUNS (it was published while open). */
  const [learnedItWaits, setLearnedItWaits] = useState<number | null>(null);
  /**
   * Running campaign: the ads this screen has told the server to remove at
   * "Publish changes". An ad that comes back (Undo) is sent again, which
   * takes the mark away.
   */
  const keptRemoved = useRef(new Set<string>());
  /** The next answer of the ad list rebuilds the screen (after a publish). */
  const rebuildNext = useRef(false);

  /*
   * Ads the server refused FOR GOOD, with what was refused and why. Such an
   * ad is not sent again until the person changes it: the same ad would be
   * refused the same way for ever (Steven, 4 Oct 2026). The other changed
   * ads are stored.
   */
  const [refused, setRefused] = useState<
    Map<string, { print: string; reason: string }>
  >(new Map());
  const refusedNow = useRef(refused);
  refusedNow.current = refused;

  /** Ads without a drawing that are at rest (see "being redrawn" below). */
  const undrawnAtRest = useRef(new Set<string>());
  const variantsNow = useRef(variants);
  variantsNow.current = variants;
  const storesItselfNow = useRef(false);

  useEffect(() => {
    const fresh = editorResponse?.editorData.adVariants;
    if (!fresh) return;

    /*
     * While the screen stores itself, a later answer of the ad list for the
     * SAME campaign never rebuilds it. A rebuild replaces every ad with the
     * server's version: what was typed in the last two seconds, an ad not
     * stored yet and the selection would all be thrown away (the rail asks
     * again every few seconds while a small picture is missing; review of
     * the foundation). Only what such an answer knows better is copied.
     */
    if (
      storesItselfNow.current &&
      adsOf === activeVacancy &&
      !rebuildNext.current
    ) {
      applyStoredPictures(fresh);
      applyFreshResources(fresh);
      return;
    }
    rebuildNext.current = false;

    const loaded = fresh.map((variant) => ({
      ...variant,
      // SYM-6185. The server's yes/no, tied to the file it is about.
      bring_to_life_resource_id: bringToLifeMarkFrom(variant),
      isDirty: false,
      isDeleted: false,
      company: variant.company ?? currentVacancy?.brand?.name, // TODO: can we remove this one?
      uuid: uniqueId(),
    }));
    // What was loaded is what the server holds.
    storedAds.current = { prints: new Map(), looks: new Map() };
    everSent.current = new Set();
    keptRemoved.current = new Set();
    setUndoEra((era) => era + 1);
    refusedNow.current = new Map();
    setRefused(refusedNow.current);
    rememberAsStored(storedAds.current, loaded);
    undrawnAtRest.current = new Set(
      loaded.filter(isBeingRedrawn).map((ad) => ad.uuid)
    );
    setAdsOf(activeVacancy ?? null);
    initVariants(loaded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorResponse?.editorData.adVariants]);

  const brandBannerTitle = currentVacancy?.brand?.default_banner_title;

  /*
   * Only when the campaign is KNOWN to be never published: its details are
   * loaded, they are this campaign's, and they say so. "Not known yet" must
   * not count as "not published": on a published campaign every store
   * reaches the ad platforms within minutes (review of the foundation). The
   * server refuses such a store as well.
   */
  const knownNeverPublished =
    !!currentVacancy &&
    currentVacancy.key === activeVacancy &&
    currentVacancy.published === false;
  /*
   * A RUNNING campaign (its job is open): the screen stores itself too, but
   * what it stores is KEPT and waits for "Publish changes" (D2; Steven,
   * 4 Oct 2026: "changes that affect the ads on the ad platform should be
   * triggered by the publish button"). Known from the campaign's details,
   * or because the server said so when it turned a plain store away.
   */
  const waitsForPublish =
    !!currentVacancy &&
    currentVacancy.key === activeVacancy &&
    // Published: running, or closed. On a CLOSED campaign the screen stores
    // itself the same way (Steven, 5 Oct 2026): what it stores is kept,
    // no ad platform is told, and it is applied when the campaign is
    // re-opened. There is no "Publish changes" in the sidebar then.
    (currentVacancy.published === true || learnedItWaits === activeVacancy);
  const waitsNow = useRef(false);
  const aiWritingNow = useRef<string | null>(null);
  aiWritingNow.current = aiWritingOn;
  waitsNow.current = waitsForPublish;
  const storesItself =
    !!activeVacancy &&
    adsOf === activeVacancy &&
    (knownNeverPublished || waitsForPublish) &&
    publishedAfterAll !== activeVacancy &&
    // On a published campaign also with NO ad on screen (every ad marked to
    // be removed, then a reload): otherwise the screen fell back to its old
    // Save button, whose store writes straight onto the stored ads and
    // queues work for the ad platforms (review of the closed-campaign slice).
    (variants.length > 0 || waitsForPublish);
  storesItselfNow.current = storesItself;
  // The toolbar leaves its older, drawing-only undo arrow out while the
  // screen has its own Undo and Redo.
  useEffect(() => {
    setPageHasUndo(storesItself);
    return () => setPageHasUndo(false);
  }, [storesItself, setPageHasUndo]);

  // After "Publish changes" the ads are loaded again: a new ad has its
  // number now, a removed one is gone. Everything was stored before the
  // publish (the sidebar waits for that), so nothing typed is lost.
  useEffect(() => {
    const again = () => {
      if (!storesItselfNow.current || !waitsNow.current) return;
      rebuildNext.current = true;
      refetchEditorData();
    };
    window.addEventListener(CHANGES_WERE_PUBLISHED, again);
    return () => window.removeEventListener(CHANGES_WERE_PUBLISHED, again);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * What a store has to send now. The ads the person changed and the new
   * ones, without those the server refused for good. On a RUNNING campaign
   * also: a deleted ad not yet marked "to be removed" (sent with that
   * mark), and an ad that came back after such a mark (sent again, which
   * takes the mark away).
   */
  const adsToSend = (): AdVariant[] => {
    const all = variantsNow.current;
    const changed = whatToStore(all, storedAds.current).toSend.filter(
      (ad) =>
        refusedNow.current.get(ad.uuid)?.print !== adPrint(ad) &&
        // AB-5: an ad the AI helper is still writing into is stored when
        // the answer has ended, never half-way.
        ad.uuid !== aiWritingNow.current
    );
    if (!waitsNow.current) return changed;

    const marked = keptRemoved.current;
    const cameBack = all.filter(
      (ad) => !ad.isDeleted && marked.has(ad.uuid) && !changed.includes(ad)
    );
    const toRemove = all.filter(
      (ad) =>
        ad.isDeleted &&
        !marked.has(ad.uuid) &&
        (!!ad.id || !!ad.kept_key || everSent.current.has(ad.uuid))
    );
    return [...changed, ...cameBack, ...toRemove];
  };
  /**
   * One ad as it travels. A deleted one (running campaign) says only WHICH
   * ad is to be removed: sent whole, an ad the server refuses (an address in
   * the wrong form) could never be removed, and nobody was told (review of
   * the running-campaign slice).
   */
  const bodyOf = (ad: AdVariant): Record<string, unknown> => {
    const whole = adForSending(ad, storedAds.current, brandBannerTitle);
    if (!ad.isDeleted) return whole;
    return {
      id: ad.id,
      uuid: whole.uuid,
      kept_key: ad.kept_key ?? undefined,
      placement: ad.placement,
      creative_type: whole.creative_type,
      banner: true,
      removed: true,
    };
  };

  /**
   * The "generate ads" screen on a running campaign: what is still on its
   * way is stored first (the marks of the deleted ads), then the generated
   * ads are kept as new ads.
   */
  // D10: the count of stores of this campaign's ads this screen builds on.
  const version = useKeptVersion('ads', activeVacancy ?? null);

  const keepGenerated = async (
    ads: Record<string, unknown>[]
  ): Promise<void> => {
    if (!activeVacancy) throw new Error('No campaign is open.');
    if (storesItselfNow.current && !(await saveNowRef.current())) {
      throw new Error('The last changes are not saved yet.');
    }
    const stamp = Date.now().toString(36);
    await saveAds(
      activeVacancy,
      ads.map((ad, place) => ({
        ...ad,
        uuid: travelName(`generated-${stamp}-${place}`),
      })),
      undefined,
      true
    );
    window.dispatchEvent(new CustomEvent(WORK_WAS_KEPT));
  };
  const saveNowRef = useRef<() => Promise<boolean>>(async () => true);

  /**
   * One save: the ads the person changed and the new ones go out, and the
   * server's answer is written onto the ads on screen. The ads on screen are
   * not replaced. DELETED ads are not deleted here: see `carryOutDeletions`.
   */
  const storeAds = useCallback(
    async (_value: unknown, signal?: AbortSignal): Promise<string> => {
      if (!activeVacancy) throw new Error('No campaign is open.');
      const campaign = activeVacancy;
      const stored = storedAds.current;
      const waits = waitsNow.current;
      const toSend = adsToSend();

      const sentPrints = new Map<string, string>();
      toSend.forEach((ad) => {
        sentPrints.set(ad.uuid, adPrint(ad));
        if (!ad.id) everSent.current.add(ad.uuid);
      });

      const storedNow: AdVariant[] = [];
      const answers: StoredAd[] = [];
      const refusedHere = new Map<string, { print: string; reason: string }>();
      const refuse = (ad: AdVariant, reason: string) => {
        refusedHere.set(ad.uuid, {
          print: sentPrints.get(ad.uuid) ?? adPrint(ad),
          reason,
        });
        // The server stored nothing of a refused new ad: it need not be
        // asked for by name when it is deleted later.
        if (!ad.id) everSent.current.delete(ad.uuid);
      };

      /*
       * Sends a group. When the server refuses it for good, the ads it named
       * are set aside and the rest goes again; when it named none, each ad
       * goes alone, so one ad at fault does not hold the others back.
       */
      const send = async (ads: AdVariant[]): Promise<void> => {
        if (!ads.length) return;
        try {
          const answer = await saveAds(
            campaign,
            ads.map(bodyOf),
            signal,
            waits,
            // D10: built on the count this screen last knew.
            {
              basedOn: version.basedOn(campaign),
              stored: (count) => version.stored(campaign, count),
            }
          );
          storedNow.push(...ads);
          answers.push(...answer);
        } catch (error) {
          if (!(error instanceof AdsRefused)) throw error;
          const refusal = error;
          if (ads.length === 1) {
            refuse(ads[0], refusal.byPlace.get(0) ?? refusal.reason);
            return;
          }
          const named = ads.filter((_ad, place) => refusal.byPlace.has(place));
          if (named.length) {
            ads.forEach((ad, place) => {
              const reason = refusal.byPlace.get(place);
              if (reason) refuse(ad, reason);
            });
            await send(ads.filter((ad) => !named.includes(ad)));
            return;
          }
          for (const ad of ads) {
            // One by one, on purpose: which ad is at fault is not known.
            // eslint-disable-next-line no-await-in-loop
            await send([ad]);
          }
        }
      };

      try {
        await send(toSend);
      } catch (error) {
        if (!(error instanceof AdsArePublished)) throw error;
        if (error.waits && !waits) {
          // The campaign runs: the same work goes again, to be kept. The
          // saver tries again by itself, and by then the screen knows.
          setLearnedItWaits(campaign);
          throw new Error('The campaign runs: sent again as work to keep.');
        }
        // Not a failure to retry: this campaign has its Save button.
        setPublishedAfterAll(campaign);
        return new Date().toISOString();
      }

      // Another campaign's ads were loaded meanwhile: this answer is not
      // about what is on screen.
      if (storedAds.current !== stored) return new Date().toISOString();
      rememberAsStored(
        stored,
        storedNow.filter((ad) => !ad.isDeleted)
      );
      if (waits) {
        storedNow.forEach((ad) => {
          if (ad.isDeleted) keptRemoved.current.add(ad.uuid);
          else keptRemoved.current.delete(ad.uuid);
        });
        // The sidebar's count of waiting changes follows.
        window.dispatchEvent(new CustomEvent(WORK_WAS_KEPT));
        // An ad a colleague's publish removed meanwhile: the screen is out
        // of date, the ads are loaded again.
        if (answers.some((one) => one.gone)) {
          rebuildNext.current = true;
          refetchEditorData();
        }
      }
      applyStoredAds(answers, sentPrints, []);

      // What is refused now: what was refused before and is still the same
      // ad, and what was refused in this save.
      const next = new Map(refusedHere);
      variantsNow.current.forEach((ad) => {
        const before = refusedNow.current.get(ad.uuid);
        if (before && !next.has(ad.uuid) && before.print === adPrint(ad)) {
          next.set(ad.uuid, before);
        }
      });
      if (next.size || refusedNow.current.size) {
        refusedNow.current = next;
        setRefused(next);
      }
      return new Date().toISOString();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeVacancy, brandBannerTitle]
  );

  /*
   * A deleted ad is deleted on the server when the person LEAVES the screen
   * (D13), not two seconds after the click: on a campaign never published a
   * delete cannot be taken back. Until then it is only off the list.
   *
   * An ad that was sent but never got its number (its answer was lost) is
   * first asked for by its name, so that it too is really deleted.
   */
  const carryOutDeletions = useCallback(
    async (campaign: number): Promise<void> => {
      const stored = storedAds.current;
      // On a running campaign nothing is deleted here: a removal waits for
      // "Publish changes" like every other change (D13), and a delete sent
      // now would pause that ad on the ad platforms at once.
      if (waitsNow.current) return;
      const deleted = variantsNow.current.filter((ad) => ad.isDeleted);
      if (!deleted.length) return;

      /*
       * From the first delete that is SENT, no Undo step may promise these
       * ads back: a delete can reach the server while its answer is lost,
       * and an ad brought back then exists on screen only (review of this
       * slice). The steps start again BEFORE anything is sent.
       */
      setUndoEra((era) => era + 1);

      const numbers = new Map<string, number>();
      const unnamed = deleted.filter(
        (ad) => !ad.id && everSent.current.has(ad.uuid)
      );
      if (unnamed.length) {
        try {
          const answer = await saveAds(
            campaign,
            unnamed.map((ad) => adForSending(ad, stored, brandBannerTitle))
          );
          answer.forEach((one) => {
            const name = screenNameOf(one.uuid);
            if (name && one.id) numbers.set(name, one.id);
          });
        } catch (error) {
          // Refused for good: the server holds nothing of them, so there is
          // nothing to delete, and the other deletions must still go.
          if (!(error instanceof AdsRefused)) throw error;
        }
      }

      for (const ad of deleted) {
        const number = ad.id ?? numbers.get(ad.uuid);
        if (!number) continue;
        try {
          // One by one: a failure leaves the rest for the next try.
          // eslint-disable-next-line no-await-in-loop
          await deleteAd(campaign, number);
        } catch (error) {
          // The campaign was published while this screen was open: nothing
          // was deleted. The screen learns it, and from now on a deleted ad
          // is a mark that waits for "Publish changes".
          if (error instanceof AdsArePublished) {
            if (error.waits) setLearnedItWaits(campaign);
            else setPublishedAfterAll(campaign);
          }
          throw error;
        }
        reallyDeleted.current.add(number);
      }

      if (storedAds.current !== stored) return;
      deleted.forEach((ad) => {
        stored.prints.delete(ad.uuid);
        stored.looks.delete(ad.uuid);
      });
      applyStoredAds(
        [],
        new Map(),
        deleted.map((ad) => ad.uuid)
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brandBannerTitle]
  );

  const sendOnTheWayOut = (campaign: number) => {
    // Without the ads the server refused for good: the server stores
    // nothing of a request that holds one, so the others' last words would
    // be lost with it (review of this slice).
    const toSend = adsToSend();
    if (!toSend.length) return;
    toSend.forEach((ad) => {
      if (!ad.id) everSent.current.add(ad.uuid);
    });
    // Real deletions do not go here: this also runs when the tab is only
    // hidden. (On a running campaign a removal is only a mark, and goes.)
    sendKeepaliveSave(
      '/v4/ad-variants',
      adsBody(campaign, toSend.map(bodyOf), waitsNow.current),
      'POST',
      version.basedOn(campaign)
    );
  };

  /*
   * A new thing at every draw, on purpose for now. Handing the saver the
   * same thing between draws (as the brand form does) was tried on 5 Oct
   * 2026 and two checks then failed: after Undo and Redo the corner stayed
   * on "Saving…" and the redone words were never sent. This screen's store
   * sends the ads as they are at that moment, not the value the saver
   * handed it, and the saver leans on being asked again at every draw to
   * notice what changed meanwhile. The price, listed in the papers: a store
   * that keeps failing is tried every two seconds here instead of at
   * growing waits.
   */
  const printsNow = storesItself ? adsPrint(variants) : undefined;
  /*
   * AB-5: while the AI helper writes a text into an ad, the saver keeps
   * seeing the screen as it was when the answer began. So nothing is stored
   * half-way; when the answer has ended the whole text is one change. An
   * answer that fails puts the old text back, and then nothing changed.
   */
  const printsBeforeAi = useRef(printsNow);
  if (!aiWritingOn) printsBeforeAi.current = printsNow;
  const printsForSaver = aiWritingOn ? printsBeforeAi.current : printsNow;
  /** Why an ad on screen is not stored, when the server refused it for good. */
  const refusedReason = printsNow
    ? variants
        .filter(
          (ad) =>
            !ad.isDeleted && refused.get(ad.uuid)?.print === printsNow[ad.uuid]
        )
        .map((ad) => refused.get(ad.uuid)?.reason)[0]
    : undefined;
  const isRefusedNow = () =>
    variantsNow.current.some(
      (ad) =>
        !ad.isDeleted && refusedNow.current.get(ad.uuid)?.print === adPrint(ad)
    );

  const {
    state: saveState,
    keptAt,
    hasPending,
    hasFailed,
    abandon,
    saveNow,
    isKept,
    refusal,
    retryRefused,
  } = useAutoSave<Record<string, string>>({
    // Only what a person changes: compared whole, the ads look changed for
    // ever (a click on another ad takes a new picture of the drawing).
    value: printsForSaver,
    // Also while a video is being made: a video only lands on an ad when it
    // is finished, and switched off the saver did not even count other edits
    // as waiting, so leaving lost them (review of the foundation).
    enabled: storesItself,
    identity: activeVacancy ? `ads:${activeVacancy}` : null,
    save: storeAds,
    flushOnHide: () => {
      if (activeVacancy) sendOnTheWayOut(activeVacancy);
    },
    // The ads on screen at a campaign change are the previous campaign's,
    // and so is the list of what the server holds of them.
    flushOnIdentityChange: (previous) => {
      const campaign = Number(String(previous).replace('ads:', ''));
      if (campaign) sendOnTheWayOut(campaign);
    },
  });

  // Deletions still to be carried out on leaving. Not on a running
  // campaign: there a removal is stored as a mark like any other change.
  const deletionsWaiting =
    storesItself && !waitsForPublish && variants.some((ad) => ad.isDeleted);
  saveNowRef.current = saveNow;

  /*
   * Undo and Redo for the whole screen (D11, D13, scene 11): ten steps, this
   * visit only. A step is what the PERSON made of the ads; the ads travel
   * with it whole, so that a step can be put back. While an ad's drawing is
   * being built again after another picture, the screen is between two
   * states and nothing is recorded.
   */
  /*
   * "Being redrawn" is the moment after a person's change. An ad that has no
   * drawing to begin with (when the screen opens), or that stays without one
   * for three seconds (no design to draw it with), is at rest: a state like
   * any other. Counted per ad: the first version took every undrawn ad for
   * "being redrawn" during the first three seconds after opening, so a
   * deletion made then was no undo step at all (first browser run on the
   * running campaign), and one ad at rest switched the guard off for all
   * ads (review of the undo slice).
   */
  const [, lookAgain] = useState(0);
  variants.forEach((ad) => {
    if (!isBeingRedrawn(ad)) undrawnAtRest.current.delete(ad.uuid);
  });
  const redrawing = variants.some(
    (ad) => isBeingRedrawn(ad) && !undrawnAtRest.current.has(ad.uuid)
  );
  useEffect(() => {
    if (!redrawing) return undefined;
    const timer = window.setTimeout(() => {
      variantsNow.current
        .filter(isBeingRedrawn)
        .forEach((ad) => undrawnAtRest.current.add(ad.uuid));
      lookAgain((n) => n + 1);
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [redrawing]);
  const betweenTwoStates = redrawing;
  const undoValue = useMemo(
    () =>
      storesItself && printsNow && !betweenTwoStates && !aiWritingOn
        ? { print: JSON.stringify(printsNow), ads: variants }
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storesItself, variants, betweenTwoStates, aiWritingOn]
  );
  const { canUndo, canRedo, undo, redo } = useUndoRedo<{
    print: string;
    ads: AdVariant[];
  }>({
    value: undoValue,
    enabled: storesItself,
    identity: activeVacancy ? `ads:${activeVacancy}:${undoEra}` : null,
    same: (a, b) => a.print === b.print,
    apply: (state) => restoreAds(state.ads),
  });

  /*
   * Leaving (a sidebar link, another campaign, "Publish Campaign"): first
   * what is on its way is stored, then the deletions are carried out. Only
   * then is everything kept.
   */
  useEffect(() => {
    if (!storesItself || !activeVacancy) return undefined;
    const campaign = activeVacancy;
    // A refused ad is work that is not stored: leaving asks, as for a save
    // that keeps failing.
    setPending((hasPending && hasFailed) || !!refusedReason);
    registerAbandon(abandon);
    registerSaveNow(
      async () => {
        if (!(await saveNow())) return false;
        if (isRefusedNow()) return false;
        try {
          await carryOutDeletions(campaign);
          return true;
        } catch (_e) {
          return false;
        }
      },
      () =>
        isKept() &&
        !isRefusedNow() &&
        (waitsNow.current
          ? adsToSend().length === 0
          : !variantsNow.current.some((ad) => ad.isDeleted))
    );
    return () => {
      setPending(false);
      registerAbandon(null);
      registerSaveNow(null);
    };
  }, [
    storesItself,
    activeVacancy,
    hasPending,
    hasFailed,
    refusedReason,
    setPending,
    registerAbandon,
    abandon,
    registerSaveNow,
    saveNow,
    isKept,
    carryOutDeletions,
  ]);

  // What used to follow the Save button follows a save that landed.
  useEffect(() => {
    if (!storesItself || saveState !== 'saved' || hasPending) return;
    refetchCheckList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveState, hasPending]);

  /*
   * Closing the tab, Back or a reload: the deleted ads are deleted on the
   * server as the page goes (Steven, 4 Oct 2026: "also delete as the tab
   * closes"). Only on the page really going away, not when the tab is merely
   * hidden: until the person leaves, a deleted ad is only off the list.
   * Nothing can be reported back, and it cannot be taken back.
   */
  const deleteOnTheWayOutRef = useRef<() => boolean>(() => false);
  deleteOnTheWayOutRef.current = () => {
    // NEVER on a running campaign: a delete there pauses the ad on the ad
    // platforms at once. Its removals wait for "Publish changes".
    if (!storesItselfNow.current || waitsNow.current || !activeVacancy) {
      return false;
    }
    const campaign = activeVacancy;
    const going = variantsNow.current.filter(
      (ad) => ad.isDeleted && !!ad.id && !reallyDeleted.current.has(ad.id)
    );
    going.forEach((ad) =>
      sendKeepaliveSave(
        `/v4/ad-variants/${ad.id}`,
        { vacancy_id: campaign, kept_by_itself: true },
        'DELETE'
      )
    );
    return going.length > 0;
  };
  useEffect(() => {
    let sentDeletes = false;
    const onPageHide = () => {
      sentDeletes = deleteOnTheWayOutRef.current() || sentDeletes;
    };
    /*
     * The browser can keep a page in memory and show it again on Back or
     * Forward. Its deleted ads are gone on the server by then, and its Undo
     * steps would promise them back: such a page is loaded afresh.
     */
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted && sentDeletes) window.location.reload();
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      /*
       * The screen itself goes away without the page going: the browser's
       * Back button to another screen of the app, or "Leave and lose
       * changes". A link click has carried out the deletions before this
       * and took those ads off the list, so nothing is sent twice.
       */
      deleteOnTheWayOutRef.current();
    };
  }, []);

  /*
   * Closing the tab or reloading (SYM-6037). The browser's own "leave this
   * page?" stays for work not stored yet: what is sent as the tab goes can be
   * too large here to be sure it arrives (a new ad carries the picture of its
   * drawing). For deleted ads it appears only where the delete above cannot
   * do its work: in Safari, where a send as the tab closes does not arrive
   * (first run in Safari's engine, 3 Oct 2026), and for an ad that was sent
   * but never got its number, which cannot be named in a delete.
   */
  const deletionsNeedThePrompt =
    storesItself &&
    !waitsForPublish &&
    variants.some(
      (ad) =>
        ad.isDeleted && (IS_SAFARI || (!ad.id && everSent.current.has(ad.uuid)))
    );
  useBeforeUnloadWarning(
    storesItself
      ? hasPending || deletionsNeedThePrompt || !!refusedReason
      : hasUnsavedChanges
  );

  /**
   * SYM-6214. Re-ask the ad list while the server is still drawing, then stop.
   *
   * Only while something is actually missing: an ad that carries a picture, or that never had a
   * design, is not waiting for anything, so a fully prepared campaign polls zero times. The counter
   * is a ref rather than state because changing it must not itself cause a render -- that would
   * re-run this effect and turn a bounded wait into a loop.
   */
  const preparingAttemptsRef = useRef(0);
  const apolloClient = useApolloClient();
  // Bumped after every re-ask, answered or not, so the next attempt is scheduled even when the answer
  // changed nothing on screen (still drawing) or failed -- otherwise the wait stopped after one.
  const [preparingTick, setPreparingTick] = useState(0);

  /*
   * ⚠️ Only while the SERVER says a saved change is being drawn. It was "any ad with a design and
   * no stored picture", which also matched ads nothing will ever draw: ads not saved since this
   * shipped, and a video ad carrying a design (the job skips videos). Bike Maker has one (ad
   * 36681), so every load of that campaign re-asked five times for pictures that could not come
   * -- measured in the QA access log, 16 Sep 2026. Those ads are drawn live instead.
   */
  const waitingForPictures = variants.some(
    (ad) => ad.preview_pending && !ad.isDeleted
  );

  /*
   * ⚠️ A SEPARATE request, not refetchEditorData(), and `no-cache` so its answer never reaches the
   * editor query. That query's answer REBUILDS the editor -- every ad replaced and given a new
   * uuid -- and a re-ask used to go through it: Steven saw the whole campaign blank a few times,
   * three seconds apart, on QA, 16 Sep 2026. A re-ask can only bring pictures, so only the picture
   * fields are copied onto the ads already on screen. If the ads differ (a save added or removed
   * one), nothing is copied: that save's own reload rebuilds. Used by the pending poll below and by
   * the link renewal after it.
   */
  const refetchStoredPictures = (): Promise<void> => {
    if (!activeVacancy) return Promise.resolve();
    return apolloClient
      .query<AdEditorQueryResponse, AdEditorQueryPayload>({
        query: GET_EDITOR_DATA,
        variables: { vacancy: activeVacancy },
        fetchPolicy: 'no-cache',
        // The header hooks/sympl-query adds to every request. Without it this answered 400 on QA
        // (16 Sep 2026, 10:18:26): apolloClient.query() bypasses that hook.
        context: {
          headers: {
            'Active-Customer': getLocalAppSession()?.activeCustomer,
          },
        },
      })
      .then((result) => {
        const fresh = result.data?.editorData.adVariants;
        if (fresh) applyStoredPictures(fresh);
      })
      .catch(() => undefined);
  };

  useEffect(() => {
    if (!waitingForPictures || !activeVacancy) {
      // Nothing pending. Reset so a later save gets its own full allowance of attempts.
      preparingAttemptsRef.current = 0;
      return;
    }

    if (preparingAttemptsRef.current >= PREPARING_MAX_ATTEMPTS) return;

    const timer = setTimeout(() => {
      preparingAttemptsRef.current += 1;

      refetchStoredPictures().finally(() =>
        setPreparingTick((tick) => tick + 1)
      );
    }, PREPARING_POLL_MS);

    return () => clearTimeout(timer);
  }, [waitingForPictures, activeVacancy, preparingTick]);

  /*
   * SYM-6214 (Astra's check D, QA, 17 Sep 2026). The stored picture links live 60 minutes. Left open
   * longer, the editor opened the designs picker with dead links: nine tiles asked for, nine
   * "Request has expired", nothing asked the API for fresh ones. So the links are renewed shortly
   * before they die, through the same picture-only request the pending poll uses, and again when
   * the tab comes back into view past that time -- a laptop asleep over lunch. Each renewal is one
   * request per campaign per hour; the generation guard in applyStoredPictures keeps a renewal from
   * ever overwriting a newer save, and a renewal touches picture fields only, never an edit.
   */
  const linksExpireAt = useMemo(
    () => earliestStoredPictureExpiry(variants),
    [variants]
  );

  /*
   * SYM-6214 (Astra's fourth review). An ad whose drawing FAILED is repaired by the server on its
   * own, 30 minutes later, at most twice. The editor stopped asking when it read `failed`, so an
   * editor left open would never learn the pictures arrived. Now: whenever the tab comes back into
   * view and some ad reads `failed`, the picture fields are re-asked once, through the same
   * generation-guarded request. No timer, no loop: one request per return to the tab, and only
   * while a failed ad is on screen.
   */
  const hasFailedAd = variants.some(
    (ad) => ad.preview_state === 'failed' && !ad.isDeleted
  );
  const lastFailedRefreshRef = useRef(0);
  useEffect(() => {
    if (!hasFailedAd || !activeVacancy) return undefined;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFailedRefreshRef.current < 60_000) return;
      lastFailedRefreshRef.current = Date.now();
      refetchStoredPictures();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [hasFailedAd, activeVacancy]);
  const lastLinkRefreshRef = useRef(0);
  useEffect(() => {
    if (!linksExpireAt || !activeVacancy) return undefined;

    const renew = () => {
      lastLinkRefreshRef.current = Date.now();
      refetchStoredPictures();
    };
    // Renew shortly before the links die, never more than once a minute, and never in the past;
    // nextLinkRenewalAt is the tested arithmetic. The renewal brings new links, which re-arm this.
    const at = nextLinkRenewalAt(
      linksExpireAt,
      lastLinkRefreshRef.current,
      Date.now()
    );
    const timer = setTimeout(renew, at - Date.now());
    const onVisible = () => {
      if (
        document.visibilityState === 'visible' &&
        nextLinkRenewalAt(
          linksExpireAt,
          lastLinkRefreshRef.current,
          Date.now()
        ) <=
          Date.now() + 1_000
      ) {
        renew();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [linksExpireAt, activeVacancy]);

  const [
    updateVariants,
    { loading: updatingVariants, error: updateVariantsError },
  ] = useMutation<{}, AdEditorUpdatePayload>(UPDATE_EDITOR_DATA);

  const [
    deleteVariants,
    { loading: deletingVariants, error: deletingVariantsError },
  ] = useMutation<{}, AdEditorDeleteVariantPayload>(DELETE_VARIANTS);

  const channelSelection = useVacancyChannelSelection(false);

  const updateAdHandler = async (): Promise<void> => {
    try {
      // Filter only dirty, non-deleted variants
      const dirtyVariants = nonDeletedVariants.filter(({ isDirty }) => isDirty);

      if (dirtyVariants.length) await updateVariantsHandler(dirtyVariants);

      const variantsToDelete = variants
        .filter(({ isDeleted }) => isDeleted)
        .filter(({ id }) => id);

      if (variantsToDelete.length) {
        // SYM-6203. One request per ad is what the server offers, but the
        // customer hears about it once, with the number. See
        // adDeletionMessage.ts.
        const outcomes = await Promise.all(
          variantsToDelete.map(({ id }) => deleteVariantsHandler(id!))
        );
        const deleted = outcomes.filter(Boolean).length;
        const { ok, text } = adDeletionMessage({
          deleted,
          failed: outcomes.length - deleted,
        });

        addToast({
          type: ok ? ToastTypes.SUCCESS : ToastTypes.ERROR,
          description: text,
        });

        if (!ok) return Promise.reject();
      }

      // SYM-6351 (Mark, 28 Sep 2026). Translated texts are now saved: only now does the "still in
      // <old language>" message go. Clearing it when Translate was pressed lost the warning for a
      // customer who left without saving.
      if (translatedUnsaved && activeVacancy) {
        setTranslatedUnsaved(false);
        await ackNotice({
          variables: {
            vacancyId: activeVacancy,
            notice: 'ad-texts-language',
            input: {},
          },
        }).catch(() => undefined);
        await refetchCampaignValues();
      }

      await refetchEditorData();
      await refetchCheckList();
    } catch (_) {}
  };

  const updateVariantsHandler = async (
    dirtyVariants: AdVariant[]
  ): Promise<void> => {
    try {
      /**
       * SYM-5926: assembled by toSavableVariant() rather than inline, so the payload can be asserted.
       * The callout used to be overwritten with the brand default here, below the spread, which threw
       * away whatever the customer had typed while the request still returned 200.
       */
      const savableVariants = dirtyVariants.map((v) =>
        toSavableVariant(v, currentVacancy?.brand?.default_banner_title)
      );

      const { errors } = await updateVariants({
        variables: {
          input: {
            vacancy_id: activeVacancy as number,
            adVariants: savableVariants,
          },
        },
      });

      if (errors) Promise.reject();

      return Promise.resolve();
    } catch (_) {
      addToast({
        type: ToastTypes.ERROR,
        description: 'An error occurred while updating the ad variants',
      });
      return Promise.reject();
    }
  };

  /**
   * Deletes one ad and says whether it went. SYM-6203: it no longer raises a
   * message of its own, because several of these run at once and the customer
   * should be told once, by the caller, with the number.
   */
  const deleteVariantsHandler = async (variantId: number): Promise<boolean> => {
    try {
      const { errors } = await deleteVariants({
        variables: {
          variantId,
          input: {
            vacancy_id: activeVacancy as number,
          },
        },
      });

      return !errors;
    } catch (_) {
      return false;
    }
  };

  const showVariantGenerator = useMemo(
    () => !loadingEditorData && !nonDeletedVariants.length,
    [nonDeletedVariants]
  );

  const { data: languages } = useApiList('languages');

  /*
   * SYM-6351. A change to the campaign's job title or location -- from the Job title row, an
   * update of live ads, or Targeting -- makes the SERVER redraw ads the editor did not touch. Each
   * new answer is a cue to fetch their pictures the picture-only way (never a rebuild, which would
   * throw away unsaved edits); applyStoredPictures takes a newer server redraw for untouched ads.
   */
  const { state: campaignValues, refetch: refetchCampaignValues } =
    useCampaignValues();
  // Translations applied as unsaved changes; cleared when saved (above) or when the editor reloads.
  const [translatedUnsaved, setTranslatedUnsaved] = useState(false);
  const [ackNotice] = useMutation<
    {},
    { vacancyId: number; notice: string; input: {} }
  >(ACK_NOTICE);
  const campaignValuesKey = campaignValues
    ? JSON.stringify([campaignValues.ad_values, campaignValues.ads])
    : null;
  const seenCampaignValuesKey = useRef<string | null>(null);
  useEffect(() => {
    if (!campaignValuesKey) return;
    const first = seenCampaignValuesKey.current === null;
    seenCampaignValuesKey.current = campaignValuesKey;
    if (!first) refetchStoredPictures();
  }, [campaignValuesKey]);

  const campaignLanguage = languages.find(
    ({ key }) => key === currentVacancy?.language_id
  )?.label;

  useEffect(() => {
    // No need to fetch other data if we can't generate payload anyways
    if (!targeting) return;

    (async () => {
      const brandsData = brands;
      const jobCategory = targeting?.sub_job_type_name;

      const language =
        languages.find(({ key }) => key === targeting?.language_id)?.label ??
        'Dutch';

      setPayload({
        jobFunction: targeting?.vac_name ?? '',
        companyName:
          brandsData?.find(({ id }) => id === targeting?.brand_id)?.name ?? '',
        location: targeting?.locations?.[0]?.name ?? 'Antwerp',
        jobCategory: jobCategory ?? '',
        experienceLevel:
          targeting?.experience
            ?.map((code) => EXPERIENCE.find(({ code: c }) => c === code)?.name)
            .join(', ') ?? 'None',
        language,
      });
    })();
  }, [targeting, languages, setPayload]);

  const checkVariantSupported = (channel: AdChannel): boolean =>
    !!currentVariant &&
    isVariantSupported(
      currentVariant.placement,
      channel,
      currentVariant.creative_type ??
        (currentVariant.placement === AdPlacement.REELS ||
          channel === AdChannel.TIKTOK)
        ? AdCreativeType.VIDEO
        : AdCreativeType.IMAGE
    );

  const displaySaveBttn = () => {
    return (
      !showVariantGenerator && (
        <SaveButton
          loading={updatingVariants}
          shouldSave={hasUnsavedChanges}
          disabled={
            updatingVariants ||
            deletingVariants ||
            !hasUnsavedChanges ||
            missingFields.length > 0 ||
            isAnyVideoRendering
          }
          error={updateVariantsError || deletingVariantsError}
          onClick={updateAdHandler}
        />
      )
    );
  };

  const showSidebar = !!activeTab && !showVariantGenerator;

  const nextStep = nextStepButton({
    storesItself,
    hasPending,
    deletionsWaiting,
    refused: !!refusedReason,
    hasUnsavedChanges,
  });

  return (
    <AppPage
      heading="Ad Builder"
      disablePadding={true}
      loading={loadingEditorData || updatingVariants || deletingVariants}
      cta={
        <div tw="flex space-x-5">
          {!!(nonDeletedVariants.length && missingFields.length) && (
            <MissingFields missingFields={missingFields} />
          )}
          {nextStep.shown && nextItem?.key === 'vacancy_page' ? (
            <Button
              variant="indigo"
              disabled={nextStep.disabled}
              onClick={() => navigate(Routes.JOB_POSTING)}
              testId="adbuilder-next-step-button"
            >
              Go to next step &rarr;
            </Button>
          ) : (
            !storesItself && displaySaveBttn()
          )}
          {storesItself && (
            <UndoRedo
              canUndo={canUndo}
              canRedo={canRedo}
              onUndo={undo}
              onRedo={redo}
            />
          )}
          <OutOfDateBox
            refusal={refusal}
            onLoadTheirs={() => {
              abandon();
              window.setTimeout(() => window.location.reload(), 100);
            }}
            onKeepMine={() => {
              version.keepMine();
              retryRefused();
            }}
          />
          {storesItself && (
            <SaveStatus
              state={saveState}
              keptAt={keptAt}
              switching={switching}
              notSavedBecause={
                refusedReason ? `Not saved: ${refusedReason}` : undefined
              }
            />
          )}
        </div>
      }
      isSideBarOpen={showSidebar}
      setActiveTab={setActiveTab}
      sideBarMenu={[
        AdEditorSideBarTabs.VISUAL_OPTIONS,
        // SYM-6252. For a customer with templates v2 the panel is about templates, so the tab says
        // so. Only the LABEL changes: the tab strip maps a non-enum item by its position, and this
        // one sits where OVERLAY_OPTIONS sits, so the active tab is still tracked as before.
        hasAdTemplatesV2 ? 'Templates' : AdEditorSideBarTabs.OVERLAY_OPTIONS,
      ]}
      activeTabIndex={
        Object.values(AdEditorSideBarTabs).indexOf(
          activeTab || AdEditorSideBarTabs.VISUAL_OPTIONS
        ) ?? 0
      }
      sideBarContent={showSidebar && <Sidebar />}
    >
      <div
        css={[
          tw`flex flex-col h-full lg:flex-row`,
          !showVariantGenerator ? tw`relative` : tw`py-20 bg-gray-50`,
        ]}
      >
        {!showVariantGenerator && (
          <AdEditorNavBar refetchEditorData={refetchEditorData} />
        )}
        <div tw="w-full h-full overflow-y-auto">
          <Centered id="container" tw="h-auto">
            {showVariantGenerator ? (
              <VariantGenerator
                refetchEditorData={() => {
                  // What was generated replaces what is on screen.
                  rebuildNext.current = true;
                  refetchEditorData();
                }}
                keepInstead={waitsForPublish ? keepGenerated : undefined}
              />
            ) : (
              <EditorWrapper id="editor-wrapper" activeTab={activeTab}>
                <PreviewContainer>
                  <div>
                    <CampaignValuesMessages
                      campaignLanguage={campaignLanguage}
                      translatedUnsaved={translatedUnsaved}
                      onTranslated={() => setTranslatedUnsaved(true)}
                    />
                    <div tw="mb-4">
                      <AdEditorToolbar
                        orientation={ToolbarOrientation.HORIZONTAL}
                        divide={false}
                        customButtons={VISUAL_AD_CHANNELS.map((channel) => {
                          const isChannelEnabled =
                            currentVacancy?.channels?.includes(channel);
                          const isSupported = checkVariantSupported(channel);
                          const tooltipText = !isSupported
                            ? `${getFormattedChannel(
                                channel
                              )} does not support ${
                                currentVariant?.placement
                              } ads`
                            : isChannelEnabled
                            ? 'Show a preview for ' +
                              getFormattedChannel(channel)
                            : `${getFormattedChannel(
                                channel
                              )} is currently not selected`;

                          return (
                            <ToolTip key={channel} text={tooltipText}>
                              <AdEditorButton
                                isActive={previewChannel === channel}
                                onClick={() => setPreviewChannel(channel)}
                                id={`channel-button-${channel.toLowerCase()}`}
                              >
                                <div tw="[svg]:p-1">
                                  <div
                                    css={[
                                      (!isChannelEnabled || !isSupported) &&
                                        tw`opacity-50 grayscale`,
                                    ]}
                                  >
                                    {getChannelIcon(channel, 32)}
                                  </div>
                                </div>
                              </AdEditorButton>
                            </ToolTip>
                          );
                        })}
                      />
                      <ChannelChangeRecovery
                        recoveries={channelSelection.recoveries}
                        retryChange={channelSelection.retryChange}
                      />
                      {previewChannel &&
                        !currentVacancy?.channels?.includes(previewChannel) &&
                        checkVariantSupported(previewChannel) && (
                          <div tw="mt-2 text-sm text-amber-600 text-center font-medium bg-amber-50 px-3 py-2 rounded-md border border-amber-200 flex items-center justify-center space-x-2">
                            <span>
                              {getFormattedChannel(previewChannel)} is currently
                              not active.
                            </span>
                            <button
                              disabled={channelSelection.disabled}
                              onClick={() =>
                                channelSelection.changeChannel(
                                  previewChannel,
                                  true
                                )
                              }
                              tw="text-amber-700 hover:text-amber-800 underline font-semibold"
                            >
                              Activate
                            </button>
                          </div>
                        )}
                    </div>

                    <AdEditorPreview />
                  </div>
                </PreviewContainer>
              </EditorWrapper>
            )}
          </Centered>
        </div>
      </div>
    </AppPage>
  );
};

const EditorWrapper = styled.div<{ activeTab?: AdEditorSideBarTabs }>`
  ${tw`relative h-full pt-4 pb-16 lg:pb-1 lg:grid`}
  ${({ activeTab }) => activeTab && tw`hidden`}
`;

const PreviewContainer = styled.div(
  tw`relative pb-5 flex flex-col-reverse items-center w-full lg:(block p-0 m-0) lg:w-full`
);

export default AdEditor;
